class CollectionTabsGrid extends HTMLElement {
  constructor() {
    super();
    this.sectionId = null;
    this.productGrid = null;
    this.tabs = null;
    this.loadingOverlay = null;
  }

  connectedCallback() {
    // Use setTimeout to ensure DOM is fully ready
    setTimeout(() => {
      this.initialize();
    }, 0);
  }

  initialize() {
    this.sectionId = this.dataset.sectionId;
    
    this.tabs = this.querySelectorAll('.collection-tab');
    this.collectionGrids = this.querySelectorAll('.collection-grid-wrapper');

    if (this.tabs.length === 0) {
      // Try again after a short delay in case tabs aren't loaded yet
      setTimeout(() => {
        this.tabs = this.querySelectorAll('.collection-tab');
        this.collectionGrids = this.querySelectorAll('.collection-grid-wrapper');
        if (this.tabs.length > 0) {
          this.setupEventListeners();
          this.checkUrlAndActivateTab();
        }
      }, 100);
      return;
    }

    if (this.collectionGrids.length === 0) {
      console.warn('No collection grids found');
      return;
    }

    this.setupEventListeners();
    
    // Set up event delegation on the tab-header as a fallback
    const tabHeader = this.querySelector('.tab-header');
    if (tabHeader) {
      tabHeader.addEventListener('click', (event) => {
        const tab = event.target.closest('.collection-tab');
        if (tab) {
          this.handleTabChange(event);
        }
      }, true);
    }
    
    // Set up event listeners for promo banner tab buttons
    this.setupPromoBannerButtons();
    
    // Check URL parameter and activate corresponding tab on page load
    this.checkUrlAndActivateTab();
    
    // Listen for browser back/forward buttons
    window.addEventListener('popstate', () => {
      this.checkUrlAndActivateTab();
    });
  }
  
  checkUrlAndActivateTab() {
    // Get collection handle from URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const collectionHandle = urlParams.get('collection');
    
    if (collectionHandle) {
      // Find the tab with matching collection handle
      const targetTab = Array.from(this.tabs).find(
        tab => tab.dataset.collectionHandle === collectionHandle
      );
      
      if (targetTab) {
        // Activate the tab and show the collection grid
        this.updateActiveTab(targetTab);
        this.showCollectionGrid(collectionHandle);
        return true;
      }
    }
    
    return false;
  }
  
  updateUrl(collectionHandle) {
    // Update URL without page reload
    const url = new URL(window.location);
    if (collectionHandle) {
      url.searchParams.set('collection', collectionHandle);
    } else {
      url.searchParams.delete('collection');
    }
    
    // Update URL without triggering page reload
    window.history.pushState({ collection: collectionHandle }, '', url);
  }
  
  setupPromoBannerButtons() {
    // Use event delegation on the entire component to catch clicks on promo banner buttons
    this.addEventListener('click', (event) => {
      const promoButton = event.target.closest('.collection-tab-button');
      if (promoButton) {
        event.preventDefault();
        event.stopPropagation();
        
        const collectionHandle = promoButton.dataset.collectionHandle;
        if (collectionHandle) {
          // Find the corresponding tab and trigger the switch
          const correspondingTab = Array.from(this.tabs).find(
            tab => tab.dataset.collectionHandle === collectionHandle
          );
          
          if (correspondingTab) {
            // Update active tab
            this.updateActiveTab(correspondingTab);
            // Show the collection grid
            this.showCollectionGrid(collectionHandle);
            // Update URL
            this.updateUrl(collectionHandle);
          } else {
            // If no corresponding tab found, just switch the grid directly
            this.showCollectionGrid(collectionHandle);
            // Update URL
            this.updateUrl(collectionHandle);
          }
        }
      }
    }, true);
  }

  setupEventListeners() {
    this.tabs.forEach((tab) => {
      // Add click handler with capture phase (runs first)
      tab.addEventListener("click", (event) => {
        this.handleTabChange(event);
      }, true);
      
      // Also add without capture as backup
      tab.addEventListener("click", (event) => {
        // Only handle if not already handled
        if (!event.defaultPrevented) {
          this.handleTabChange(event);
        }
      }, false);
    });
  }

  updateActiveTab(target) {
    this.tabs.forEach(tab => tab.classList.remove('active'));
    target.classList.add('active');
  }


  handleTabChange(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation(); // Also stop immediate propagation
    
    const target = event.target.closest('.collection-tab');
    
    if (!target) {
      return;
    }

    const collectionHandle = target.dataset.collectionHandle;

    if (!collectionHandle) {
      return;
    }
    
    // Update active tab
    this.updateActiveTab(target);
    
    // Show/hide collection grids
    this.showCollectionGrid(collectionHandle);
    
    // Update URL
    this.updateUrl(collectionHandle);
  }

  showCollectionGrid(collectionHandle) {
    // Hide all collection grids
    this.collectionGrids.forEach(grid => {
      grid.classList.add('hidden');
    });
    
    // Show the selected collection grid
    const targetGrid = Array.from(this.collectionGrids).find(
      grid => grid.dataset.collectionHandle === collectionHandle
    );
    
    if (targetGrid) {
      targetGrid.classList.remove('hidden');
      
      // Update promo banner button states
      this.updatePromoBannerButtons(collectionHandle);
      
      // Re-initialize any widgets that need it (like rating widgets)
      this.reinitializeWidgets();
    }
  }

  updatePromoBannerButtons(activeCollectionHandle) {
    // Find all promo banner buttons
    const promoButtons = this.querySelectorAll('.collection-tab-button');
    
    promoButtons.forEach(button => {
      const buttonCollectionHandle = button.dataset.collectionHandle;
      const icon = button.querySelector('.promo-banner__tab-button-icon');
      
      if (buttonCollectionHandle === activeCollectionHandle) {
        // Make button active (white background)
        button.classList.add('active');
      } else {
        // Make button inactive (transparent with border)
        button.classList.remove('active');
      }
    });
  }

  reinitializeWidgets() {
    // Re-initialize rating widgets if they exist
    if (typeof window.initRatingWidgets === 'function') {
      window.initRatingWidgets();
    }
    
    // Trigger any custom events that might be needed
    const event = new CustomEvent('collection-tab-changed', {
      detail: { sectionId: this.sectionId }
    });
    document.dispatchEvent(event);
  }
}

// Define the custom element immediately (before DOMContentLoaded)
// This ensures it's available when the HTML is parsed
if (!customElements.get("collection-tabs-grid")) {
  customElements.define("collection-tabs-grid", CollectionTabsGrid);
}

